import { DAppConnector } from "@hashgraph/hedera-wallet-connect";
import { HederaNetwork, WalletLoadedEvent, WalletLockedEvent, WalletUpdatedEvent } from "./models/blade";
import { noSessionError } from "./models/errors";
import { LedgerId } from "@hashgraph/sdk";
import { filter } from "rxjs";
import { getAccountIDsFromSigners } from "./helpers/utils";
export class WCConnector {
    constructor(meta) {
        this.activeSigner = null;
        this.signers = [];
        this.subscriptions = [];
        this.dAppConnector = new DAppConnector(meta);
        this.dAppConnector.init([WalletLoadedEvent, WalletUpdatedEvent, WalletLockedEvent]);
    }
    /**
     * @param callback synchronous function to run every time wallet is locked
     */
    onWalletLocked(callback) {
        const lockedSub = this.dAppConnector.$events
            .pipe(filter((e) => e.name === WalletLockedEvent))
            .subscribe(callback);
        this.subscriptions.push(lockedSub);
    }
    onAccountChanged(callback) {
        const updatedSub = this.dAppConnector.$events
            .pipe(filter((e) => e.name === WalletUpdatedEvent))
            .subscribe(callback);
        this.subscriptions.push(updatedSub);
    }
    async createSession(params) {
        try {
            const networkName = ((params === null || params === void 0 ? void 0 : params.network) || HederaNetwork.Mainnet).toLowerCase();
            await this.dAppConnector.connect(LedgerId.fromString(networkName));
            this.signers = this.dAppConnector.getSigners();
            this.activeSigner = this.signers[0] || null;
            return getAccountIDsFromSigners(this.signers);
        }
        catch (e) {
            throw e;
        }
    }
    async killSession() {
        this.subscriptions.forEach(s => s.unsubscribe());
        return this.dAppConnector.disconnect();
    }
    call(executable) {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.call(executable);
    }
    checkTransaction(transaction) {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.checkTransaction(transaction);
    }
    getAccountBalance() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getAccountBalance();
    }
    getAccountId() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getAccountId();
    }
    getAccountInfo() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getAccountInfo();
    }
    getAccountRecords() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getAccountRecords();
    }
    getLedgerId() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getLedgerId();
    }
    getMirrorNetwork() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getMirrorNetwork();
    }
    getNetwork() {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.getNetwork();
    }
    populateTransaction(transaction) {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.populateTransaction(transaction);
    }
    signTransaction(transaction) {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.signTransaction(transaction);
    }
    sign(messages, signOptions) {
        if (!this.activeSigner) {
            throw noSessionError();
        }
        return this.activeSigner.sign(messages, signOptions);
    }
    async selectAccount(accountId) {
        const account = this.signers.find(s => s.getAccountId().toString() === accountId);
        if (!this.activeSigner || !account && accountId) {
            throw noSessionError();
        }
        this.activeSigner = account || this.activeSigner;
        return this.activeSigner;
    }
    get initialized() {
        var _a;
        return Boolean((_a = this.dAppConnector) === null || _a === void 0 ? void 0 : _a.initialized);
    }
}
